/**
 * What the chrome says around creator onboarding (D165b) — in one place,
 * named by the fact it answers, in the account control's register.
 *
 * @remarks
 * These lines live on the player app only until the studio exists: the
 * trigger is behind `?creator=onboard`, never a button a player can see,
 * and Stripe sends the browser back to the same page. When onboarding
 * moves to `studio.orpheusquest.com` this file moves with it, and the
 * store's doors do not change — which is the whole reason the words are
 * kept apart from the wiring.
 *
 * **The reader here is not a player.** They are somebody setting up to be
 * paid, and the register shifts with them: still plain and second person,
 * but the facts that matter are about money arriving rather than a game
 * continuing. Nothing in this file may borrow the fiction's voice, for the
 * same reason the account door may not — and one harder reason. A page
 * that collects bank details in a character's voice is the exact costume
 * a fraud wears, and this is the one flow in the product where somebody is
 * about to hand over an account number.
 *
 * @packageDocumentation
 */

import { ONBOARD_REFUSALS, type OnboardRefusal } from '@orpheus/api-contracts';

import type { CheckoutRefusal } from './store-client.js';

import { CHECKOUT_REFUSED } from './store-copy.js';

/**
 * What the chrome says at each step of becoming a creator.
 *
 * @remarks
 * `notYetPayable` is the hard one, because the platform genuinely cannot
 * tell WHY. `isPayable` is `charges_enabled && payouts_enabled`, and a
 * false reads the same whether Stripe is still waiting on a document or
 * still reviewing one it has. So the line names both possibilities and
 * blames neither — "it may still need something from you, or may still be
 * checking" is the whole truth available, and inventing a more specific
 * one would be guessing at a stranger's paperwork.
 *
 * It also has to defuse "start again", which is what the affordance
 * unavoidably looks like: Stripe's onboarding links are one-time and
 * short-lived, so resuming means fetching a NEW link, and a creator who
 * reads "start again" reasonably fears re-entering everything. It resumes.
 * Saying so is the difference between a dead end and a next step.
 *
 * `payable` names both halves of `isPayable` rather than saying "you are
 * all set", because they are two different permissions and a creator who
 * later cannot be paid should be able to remember which one they were
 * told they had.
 */
export const CREATOR_ONBOARDING = {
	/** Stripe sent the browser back, and will not pay the account yet — onboarding is unfinished, or under review. */
	notYetPayable:
		'Stripe has your details and is not ready to pay you yet — it may still need something from you, or may still be checking. Starting again picks up where you left off.',
	/** Stripe sent the browser back, and the account can be charged and paid. */
	payable: 'You are set up as a creator — Stripe can charge for your games and pay you for them.',
	/** The ask arrived; Stripe's page is being fetched. */
	starting: 'Taking you to Stripe to set up payouts…',
} as const;

/**
 * The refusals whose purchase-flavored wording is WRONG during onboarding.
 *
 * @remarks
 * `CHECKOUT_REFUSED` serves both doors, and six of its sentences reassure
 * the reader that **nothing was charged** — which is exactly right when a
 * checkout failed and is nonsense here, where no payment was ever in
 * flight. Telling somebody setting up payouts that their card is safe
 * invents a worry out of nothing and, worse, suggests the platform is
 * confused about what they were doing. `return_url_not_allowed` is wrong
 * for a plainer reason: it says "checkout", and this was not one.
 *
 * The `store-api` onboard door can return exactly seven: `not_signed_in`,
 * `not_an_onboard_request`, `not_json`, `not_found`,
 * `return_url_not_allowed`, `stripe_unavailable`, and the shell's own
 * `unreachable`. Five are re-said here; `not_signed_in` gets an onboarding
 * sentence of its own, and `not_an_onboard_request` keeps the shared line
 * BY REFERENCE — it was written for this door in the first place, so the
 * two must not be allowed to drift apart by being typed twice.
 *
 * **The map is TOTAL over what the door can answer** (Lyra, D165c, closing
 * the gap Calder marked): `OnboardRefusal` in `api-contracts` names exactly
 * the refusals `POST /creators/onboard` can return, plus the shell's own
 * `unreachable`, so a refusal the door learns tomorrow is a compile error
 * here today rather than a purchase sentence read to somebody setting up
 * payouts. `not_an_onboard_request` was written for this door and keeps
 * its shared line by reference; the fall-through below is for the
 * impossible rest of the wider `CheckoutRefusal`, which the transport type
 * still admits.
 */
const ONBOARDING_REFUSED: Readonly<Record<OnboardRefusal | 'unreachable', string>> = {
	not_an_onboard_request: CHECKOUT_REFUSED.not_an_onboard_request,
	not_found: 'Something went wrong setting this up. Try again in a moment.',
	not_json: 'Something went wrong setting this up. Try again in a moment.',
	not_signed_in: 'Sign in first — payouts belong to an account, not to a browser.',
	return_url_not_allowed: 'Setting up payouts cannot be started from this page.',
	stripe_unavailable: 'Stripe is not reachable right now. Nothing has changed about your account — try again shortly.',
	unreachable: 'The store could not be reached. Nothing has changed about your account — check your connection and try again.',
};

/** Whether the door can answer with this — the contract's list, plus the shell's own silence. */
const isOnboardingReachable = (refusal: CheckoutRefusal): refusal is OnboardRefusal | 'unreachable' =>
	refusal === 'unreachable' || (ONBOARD_REFUSALS as readonly string[]).includes(refusal);

/**
 * Why onboarding could not start, in the register of the person reading.
 *
 * @param refusal - What the door answered
 * @returns The onboarding wording for everything the door can say, and the shared line for anything the transport type still admits
 */
export const onboardingRefusedLine = (refusal: CheckoutRefusal): string => (isOnboardingReachable(refusal) ? ONBOARDING_REFUSED[refusal] : CHECKOUT_REFUSED[refusal]);
